// Thin wrapper over Web Speech speechSynthesis. The auto-play preference
// persists in localStorage so it survives reloads. M4 default = on; the M6
// settings page will surface a toggle.

const STORAGE_KEY = 'mw.tts.autoplay';

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem' | 'removeItem'>;
type SpeakOpts = { lang?: string; rate?: number; voiceURI?: string | null };
type SpeakFn = (text: string, opts?: SpeakOpts) => void;

export type Tts = {
  isAutoplayOn(): boolean;
  setAutoplay(on: boolean): void;
  speak: SpeakFn;
  cancel(): void;
};

type Deps = {
  storage?: Storage | null;
  synth?: SpeechSynthesis | null;
  Utterance?: typeof SpeechSynthesisUtterance;
};

export function createTts(deps: Deps = {}): Tts {
  const storage =
    deps.storage === undefined && typeof localStorage !== 'undefined'
      ? localStorage
      : (deps.storage ?? null);
  const synth =
    deps.synth === undefined && typeof speechSynthesis !== 'undefined'
      ? speechSynthesis
      : (deps.synth ?? null);
  const Utterance = deps.Utterance ?? (typeof SpeechSynthesisUtterance !== 'undefined' ? SpeechSynthesisUtterance : undefined);

  function isAutoplayOn(): boolean {
    if (!storage) return true;
    const v = storage.getItem(STORAGE_KEY);
    if (v === null) return true; // default on
    return v === '1';
  }

  function setAutoplay(on: boolean): void {
    if (!storage) return;
    storage.setItem(STORAGE_KEY, on ? '1' : '0');
  }

  const speak: SpeakFn = (text, opts = {}) => {
    if (!synth || !Utterance) return;
    const u = new Utterance(text);
    u.lang = opts.lang ?? 'en-US';
    u.rate = opts.rate ?? 1.0;
    if (opts.voiceURI) {
      const voices = synth.getVoices();
      const match = voices.find((v) => v.voiceURI === opts.voiceURI);
      if (match) u.voice = match;
    }
    synth.speak(u);
  };

  function cancel(): void {
    if (!synth) return;
    synth.cancel();
  }

  return { isAutoplayOn, setAutoplay, speak, cancel };
}

export function speakIfAutoOn(tts: Tts, text: string, opts?: SpeakOpts): void {
  if (tts.isAutoplayOn()) tts.speak(text, opts);
}
